import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import {
  createRoom,
  getOwnerRoomDetail,
  updateRoom,
  type OwnerRoomDetail,
  type RoomImageTicket,
  type RoomUpsertRequest,
} from '../../api/partnerRooms';
import { useToast } from '../../components/ui/ToastProvider';
import { uploadPartnerImage } from '../../lib/partnerUpload';

type FacilityRow = { facilityKey: string; label: string };
type OptionRow = { name: string; description: string; price: string };
type ExistingImage = { imageUrl: string; mediaId: string | null };

function errText(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function PartnerRoomEditPage() {
  const navigate = useNavigate();
  const { studioId, roomId } = useParams();
  const isNew = !roomId;
  const { showToast } = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [detail, setDetail] = useState<OwnerRoomDetail | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [categoryLabel, setCategoryLabel] = useState('');
  const [description, setDescription] = useState('');
  const [capacityMin, setCapacityMin] = useState('1');
  const [capacityMax, setCapacityMax] = useState('4');
  const [basePrice, setBasePrice] = useState('');
  const [priceUnit, setPriceUnit] = useState('시간');
  const [bookingMethod, setBookingMethod] = useState('INSTANT');
  const [isListed, setIsListed] = useState(true);

  const [facilities, setFacilities] = useState<FacilityRow[]>([]);
  const [options, setOptions] = useState<OptionRow[]>([]);

  const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
  const [newImages, setNewImages] = useState<RoomImageTicket[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    getOwnerRoomDetail(roomId!)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setName(data.title ?? '');
        setSlug(data.slug ?? '');
        setCategoryLabel(data.category ?? '');
        setDescription(data.description ?? '');
        setCapacityMin(data.capacityMin != null ? String(data.capacityMin) : '1');
        setCapacityMax(data.capacityMax != null ? String(data.capacityMax) : '4');
        setBasePrice(data.basePrice != null ? String(data.basePrice) : '');
        setPriceUnit(data.priceUnit ?? '시간');
        setBookingMethod(data.bookingMethod ?? 'INSTANT');
        setIsListed(data.isListed ?? true);
        setFacilities(
          (data.facilityChips ?? []).map((chip) => ({
            facilityKey: chip.key ?? '',
            label: chip.label,
          })),
        );
        setOptions(
          (data.options ?? []).map((option) => ({
            name: option.name,
            description: option.description ?? '',
            price: String(option.priceWon ?? 0),
          })),
        );
        const images =
          data.images && data.images.length > 0
            ? data.images
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((image) => ({
                  imageUrl: image.imageUrl ?? '',
                  mediaId: image.mediaId,
                }))
                .filter((image) => image.imageUrl)
            : (data.galleryUrls ?? []).map((url) => ({ imageUrl: url, mediaId: null }));
        setExistingImages(images);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(errText(error, '룸 정보를 불러오지 못했습니다.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNew, roomId]);

  const onImageSelected = async (file: File | null) => {
    if (!file || !studioId) return;
    setUploadingImage(true);
    try {
      const uploaded = await uploadPartnerImage(file, {
        targetType: 'ROOM_GALLERY_IMAGE',
        studioId,
      });
      setNewImages((prev) => [...prev, uploaded]);
      showToast('이미지를 업로드했습니다. 저장 시 반영됩니다.');
    } catch (error) {
      showToast(errText(error, '이미지 업로드에 실패했습니다.'));
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const buildRequest = (): RoomUpsertRequest => ({
    slug: slug.trim() || undefined,
    name: name.trim(),
    description: description.trim() || undefined,
    categoryLabel: categoryLabel.trim() || undefined,
    capacityMin: capacityMin.trim() ? Number(capacityMin) : undefined,
    capacityMax: capacityMax.trim() ? Number(capacityMax) : undefined,
    basePrice: basePrice.trim() ? Number(basePrice) : undefined,
    priceUnit: priceUnit.trim() || undefined,
    imageUrls: existingImages.map((image) => image.imageUrl),
    imageTickets: newImages.length > 0 ? newImages : undefined,
    facilities: facilities
      .map((row) => ({ facilityKey: row.facilityKey.trim(), label: row.label.trim() }))
      .filter((row) => row.label),
    options: options
      .map((row) => ({
        name: row.name.trim(),
        description: row.description.trim() || undefined,
        price: row.price.trim() ? Number(row.price) : 0,
      }))
      .filter((row) => row.name),
    // 콘솔 v1 미편집 항목은 기존 값 보존 — 수정 요청에서 누락되면 초기화될 수 있다.
    reservationFields: detail?.reservationFields?.map((field) => ({
      inputType: field.inputType,
      title: field.title,
      required: field.required,
      inputFrequency: field.inputFrequency ?? undefined,
      sortOrder: field.sortOrder,
      options: field.options,
    })),
    timeSlotPrices: detail?.timeSlotPrices ?? undefined,
    bookingMethod: bookingMethod || undefined,
    isListed,
  });

  const submit = async () => {
    if (!name.trim()) {
      showToast('룸 이름을 입력하세요.');
      return;
    }
    if (!basePrice.trim() || Number.isNaN(Number(basePrice))) {
      showToast('기본 가격을 숫자로 입력하세요.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        if (!studioId) throw new Error('스튜디오 정보가 없습니다.');
        const created = await createRoom(studioId, buildRequest());
        showToast('룸을 등록했습니다.');
        navigate(`/partner/rooms/${created.roomId}`, { replace: true });
      } else {
        await updateRoom(roomId!, buildRequest());
        showToast('룸을 저장했습니다.');
        navigate(-1);
      }
    } catch (error) {
      showToast(errText(error, '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="partner-empty">불러오는 중…</p>;
  }
  if (loadError) {
    return <div className="partner-error">{loadError}</div>;
  }

  return (
    <>
      <h1 className="partner-title">{isNew ? '룸 등록' : '룸 수정'}</h1>
      <p className="partner-subtitle">
        {isNew ? '새 룸의 기본 정보를 입력하세요.' : `${detail?.title ?? ''} 정보를 수정합니다.`}
      </p>

      <div className="partner-card">
        <h2 className="partner-card__title">기본 정보</h2>
        <div className="partner-form">
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">룸 이름 *</span>
              <input onChange={(event) => setName(event.target.value)} type="text" value={name} />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">
                슬러그 <small>(URL 식별자)</small>
              </span>
              <input onChange={(event) => setSlug(event.target.value)} type="text" value={slug} />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">카테고리</span>
              <input
                onChange={(event) => setCategoryLabel(event.target.value)}
                placeholder="합주실"
                type="text"
                value={categoryLabel}
              />
            </label>
          </div>
          <label className="partner-field">
            <span className="partner-field__label">소개</span>
            <textarea
              onChange={(event) => setDescription(event.target.value)}
              value={description}
            />
          </label>
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">최소 인원</span>
              <input
                min={1}
                onChange={(event) => setCapacityMin(event.target.value)}
                type="number"
                value={capacityMin}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">최대 인원</span>
              <input
                min={1}
                onChange={(event) => setCapacityMax(event.target.value)}
                type="number"
                value={capacityMax}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">기본 가격 (원) *</span>
              <input
                min={0}
                onChange={(event) => setBasePrice(event.target.value)}
                type="number"
                value={basePrice}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">가격 단위</span>
              <input
                onChange={(event) => setPriceUnit(event.target.value)}
                placeholder="시간"
                type="text"
                value={priceUnit}
              />
            </label>
          </div>
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">예약 방식</span>
              <select
                onChange={(event) => setBookingMethod(event.target.value)}
                value={bookingMethod}
              >
                <option value="INSTANT">즉시 예약</option>
                <option value="APPROVAL">승인 후 확정</option>
              </select>
            </label>
            <label className="partner-field partner-field--inline" style={{ alignSelf: 'end' }}>
              <input
                checked={isListed}
                onChange={(event) => setIsListed(event.target.checked)}
                type="checkbox"
              />
              <span className="partner-field__label">탐색/검색에 노출</span>
            </label>
          </div>
        </div>
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">이미지</h2>
        {existingImages.length + newImages.length > 0 ? (
          <div className="partner-gallery" style={{ marginBottom: 12 }}>
            {existingImages.map((image, index) => (
              <div className="partner-gallery__item" key={`existing-${index}`}>
                <img alt="" src={image.imageUrl} />
                <button
                  aria-label="이미지 제거"
                  className="partner-gallery__remove"
                  onClick={() =>
                    setExistingImages((prev) => prev.filter((_, i) => i !== index))
                  }
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
            {newImages.map((image, index) => (
              <div className="partner-gallery__item" key={`new-${image.mediaId}`}>
                <img alt="" src={image.imageUrl} />
                <span className="partner-gallery__badge">신규</span>
                <button
                  aria-label="이미지 제거"
                  className="partner-gallery__remove"
                  onClick={() => setNewImages((prev) => prev.filter((_, i) => i !== index))}
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="partner-card__hint">등록된 이미지가 없습니다.</p>
        )}
        <input
          accept="image/*"
          hidden
          onChange={(event) => onImageSelected(event.target.files?.[0] ?? null)}
          ref={imageInputRef}
          type="file"
        />
        <button
          className="partner-btn partner-btn--ghost partner-btn--sm"
          disabled={uploadingImage}
          onClick={() => imageInputRef.current?.click()}
          type="button"
        >
          {uploadingImage ? '업로드 중…' : '+ 이미지 추가'}
        </button>
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">시설/편의</h2>
        <div className="partner-rows">
          {facilities.map((row, index) => (
            <div className="partner-row-item" key={index}>
              <input
                aria-label="시설 키"
                onChange={(event) =>
                  setFacilities((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, facilityKey: event.target.value } : item,
                    ),
                  )
                }
                placeholder="키 (예: parking, wifi)"
                type="text"
                value={row.facilityKey}
              />
              <input
                aria-label="시설 이름"
                onChange={(event) =>
                  setFacilities((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, label: event.target.value } : item,
                    ),
                  )
                }
                placeholder="이름 (예: 주차 가능)"
                type="text"
                value={row.label}
              />
              <button
                className="partner-btn partner-btn--danger partner-btn--sm"
                onClick={() => setFacilities((prev) => prev.filter((_, i) => i !== index))}
                type="button"
              >
                삭제
              </button>
            </div>
          ))}
        </div>
        <button
          className="partner-btn partner-btn--ghost partner-btn--sm"
          onClick={() => setFacilities((prev) => [...prev, { facilityKey: '', label: '' }])}
          style={{ marginTop: 10 }}
          type="button"
        >
          + 시설 추가
        </button>
      </div>

      <div className="partner-card">
        <h2 className="partner-card__title">추가 옵션</h2>
        <p className="partner-card__hint">예약 시 함께 선택할 수 있는 유료 옵션입니다.</p>
        <div className="partner-rows">
          {options.map((row, index) => (
            <div className="partner-row-item" key={index}>
              <input
                aria-label="옵션 이름"
                onChange={(event) =>
                  setOptions((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, name: event.target.value } : item,
                    ),
                  )
                }
                placeholder="옵션 이름"
                type="text"
                value={row.name}
              />
              <input
                aria-label="옵션 설명"
                onChange={(event) =>
                  setOptions((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, description: event.target.value } : item,
                    ),
                  )
                }
                placeholder="설명"
                type="text"
                value={row.description}
              />
              <input
                aria-label="옵션 가격"
                min={0}
                onChange={(event) =>
                  setOptions((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, price: event.target.value } : item,
                    ),
                  )
                }
                placeholder="가격(원)"
                style={{ maxWidth: 140 }}
                type="number"
                value={row.price}
              />
              <button
                className="partner-btn partner-btn--danger partner-btn--sm"
                onClick={() => setOptions((prev) => prev.filter((_, i) => i !== index))}
                type="button"
              >
                삭제
              </button>
            </div>
          ))}
        </div>
        <button
          className="partner-btn partner-btn--ghost partner-btn--sm"
          onClick={() =>
            setOptions((prev) => [...prev, { name: '', description: '', price: '' }])
          }
          style={{ marginTop: 10 }}
          type="button"
        >
          + 옵션 추가
        </button>
      </div>

      <div className="partner-form__actions">
        <button
          className="partner-btn partner-btn--ghost"
          onClick={() => navigate(-1)}
          type="button"
        >
          취소
        </button>
        <button className="partner-btn" disabled={saving} onClick={submit} type="button">
          {saving ? '저장 중…' : isNew ? '룸 등록' : '저장'}
        </button>
      </div>
    </>
  );
}
